import React from 'react';
import { Check, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatNmiCardBrandLabel } from '@/utils/nmiCardDisplay';
import { savedCardPlatinum } from './PaymentCardGradientStyles';

/**
 * Polished saved-card row — shared by Stripe pickers and NMI card-on-file display.
 * The selected / on-file card is platinum, like every saved card (PaymentCardGradientStyles.js).
 */
export default function SavedPaymentMethodCard({
  brand,
  last4,
  expMonth,
  expYear,
  selected = false,
  readOnly = false,
  onClick = null,
  badge = null,
  className = '',
}) {
  if (!last4) return null;

  const interactive = !readOnly && typeof onClick === 'function';
  const Tag = interactive ? 'button' : 'div';
  const brandLabel = formatNmiCardBrandLabel(brand);
  const expLabel = expMonth && expYear
    ? `Exp ${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}`
    : null;
  const showAccent = selected || readOnly;

  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={interactive ? onClick : undefined}
      className={cn(
        'group w-full flex items-center gap-3.5 p-3.5 sm:p-4 rounded-xl border transition-all duration-200 text-left',
        showAccent
          ? savedCardPlatinum.rowAccent
          : 'border-white/[0.08] bg-white/[0.025] hover:border-white/15 hover:bg-white/[0.045]',
        interactive && 'cursor-pointer',
        readOnly && 'cursor-default',
        className,
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border',
          showAccent
            ? savedCardPlatinum.iconAccent
            : 'bg-white/[0.05] border-white/[0.08] group-hover:bg-white/[0.08]',
        )}
      >
        <CreditCard className={cn('w-5 h-5', showAccent ? savedCardPlatinum.iconColorAccent : 'text-white/45')} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white text-sm font-medium tracking-tight">{brandLabel}</span>
          {badge ? (
            <span className={cn('text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full', savedCardPlatinum.badge)}>
              {badge}
            </span>
          ) : null}
        </div>
        <div className="flex items-baseline gap-x-3 gap-y-0.5 mt-1 flex-wrap">
          <span className="text-white/50 font-mono text-[13px] tracking-[0.08em]">•••• {last4}</span>
          {expLabel ? (
            <span className="text-white/40 text-[11px] tabular-nums uppercase tracking-wide">{expLabel}</span>
          ) : null}
        </div>
      </div>

      {selected && !readOnly ? (
        <div className={cn('flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center', savedCardPlatinum.check)}>
          <Check className={cn('w-3.5 h-3.5', savedCardPlatinum.checkIcon)} strokeWidth={2.5} />
        </div>
      ) : null}

      {readOnly && !badge ? (
        <div className={cn('flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center', savedCardPlatinum.check)}>
          <Check className={cn('w-3.5 h-3.5', savedCardPlatinum.checkIcon)} strokeWidth={2.5} />
        </div>
      ) : null}
    </Tag>
  );
}

/** Map Stripe payment method objects to SavedPaymentMethodCard props. */
export function stripePaymentMethodToCardProps(pm) {
  if (!pm) return null;
  return {
    brand: pm.brand,
    last4: pm.last4,
    expMonth: pm.exp_month ?? pm.expMonth,
    expYear: pm.exp_year ?? pm.expYear,
  };
}
